import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaFacebook, FaInstagram, FaWhatsapp,
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaHeart 
} from 'react-icons/fa';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: <FaFacebook />, url: 'https://www.facebook.com/profile.php?id=61583258431411', label: 'Facebook' },
    { icon: <FaInstagram />, url: 'https://www.instagram.com/navya_thehomestay?igsh=MTkwNWg3ZjNyemFpdw==', label: 'Instagram' },
    { icon: <FaWhatsapp />, url: 'https://wa.me/919420084600', label: 'WhatsApp' },
  ];

  const quickLinks = [
    { name: 'About Us', href: '#about' },
    { name: 'Rooms & Tariff', href: '#rooms' },
    { name: 'Amenities', href: '#amenities' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Reviews', href: '#reviews' },
    { name: 'Contact', href: '#contact' },
  ];

  const policies = [
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Terms & Conditions', href: '/terms-conditions' },
    { name: 'House Rules', href: '#house-rules' },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <img 
              src={require('../assets/logo.png')} 
              alt="Navyaa The Home Stay" 
              className="h-20 w-auto mb-4"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div style={{ display: 'none' }} className="mb-4">
              <h3 className="text-3xl font-script text-primary-400 mb-2">Navyaa</h3>
              <p className="text-lg font-elegant text-gray-300">The Home Stay</p>
            </div>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Experience nature's embrace at Navyaa - where comfort meets serenity. 
              Your perfect getaway destination.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  whileHover={{ scale: 1.2, y: -3 }}
                  className="bg-primary-500 hover:bg-primary-400 text-white p-2 rounded-full transition-colors"
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold mb-4 text-primary-400">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-primary-400 transition-colors inline-block hover:translate-x-1 transform duration-200"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Policies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold mb-4 text-primary-400">Policies</h3>
            <ul className="space-y-2">
              {policies.map((policy, index) => (
                <li key={index}>
                  <a
                    href={policy.href}
                    className="text-gray-400 hover:text-primary-400 transition-colors inline-block hover:translate-x-1 transform duration-200"
                  >
                    {policy.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold mb-4 text-primary-400">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <FaMapMarkerAlt className="text-primary-400 mt-1 flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  79, near NMC Zonal Office,<br />
                  opposite Vrundavan Apartments, Laxminagar,<br />
                  Nagpur, Maharashtra 440022<br />
                  India
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <FaPhone className="text-primary-400 flex-shrink-0" />
                <a href="tel:+919420084600" className="text-gray-400 hover:text-primary-400 transition-colors text-sm">
                  +91 94200 84600
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <FaEnvelope className="text-primary-400 flex-shrink-0" />
                <a href="mailto:mohiteji@gmail.com" className="text-gray-400 hover:text-primary-400 transition-colors text-sm">
                  mohiteji@gmail.com
                </a>
              </li>
            </ul>

            {/* WhatsApp CTA */}
            <motion.a
              href="https://wa.me/919420084600"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-6 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-full inline-flex items-center space-x-2 transition-colors shadow-lg"
            >
              <FaWhatsapp size={20} />
              <span className="font-semibold">Chat with Us</span>
            </motion.a>
          </motion.div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Navyaa The Home Stay. All rights reserved.
            </p>
            <p className="text-gray-400 text-sm flex items-center">
              Made with <FaHeart className="text-red-500 mx-1 animate-pulse" /> for Nature Lovers
            </p>
          </div>
        </div>
      </div>

      {/* Decorative Bottom Border */}
      <div className="h-2 bg-gradient-to-r from-primary-500 via-nature-leaf to-primary-500"></div>
    </footer>
  );
};

export default Footer;
